import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { listProfileTabs, mapApiProfileTabToUi } from '../../../services/profileTabs.js';
import { OVERVIEW_TAB } from '../constants/profileTabs.js';

/**
 * The tabs a profile opens with: Overview, then whatever the endpoint returns.
 *
 * A failure here is deliberately NOT surfaced as an error state. Losing the
 * extra tabs is a degradation; losing the profile is an outage, and the two
 * should not have the same blast radius. So the hook falls back to Overview
 * alone and keeps `error` for a caller that wants to report it quietly.
 */
export function useProfileTabs({ entityType } = {}) {
  const { t } = useTranslation('unified-profiles');
  const [extraTabs, setExtraTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchTabs = useCallback(async () => {
    if (!entityType) return;
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const { body } = await listProfileTabs({ entityType });
      if (requestId !== fetchIdRef.current) return;
      setExtraTabs((body?.items ?? []).map(mapApiProfileTabToUi).filter((tab) => tab.id));
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setExtraTabs([]);
      setError(err?.message ?? 'Failed to load profile tabs');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    fetchTabs();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchTabs]);

  const tabs = useMemo(
    () => [
      { ...OVERVIEW_TAB, label: t(OVERVIEW_TAB.labelKey) },
      // Defensive: the endpoint owning "everything except Overview" is a
      // convention, and a backend that starts returning it would otherwise
      // render two Overview tabs.
      ...extraTabs.filter((tab) => tab.id !== OVERVIEW_TAB.id),
    ],
    [extraTabs, t],
  );

  return useMemo(() => ({ tabs, loading, error, refetch: fetchTabs }), [
    tabs,
    loading,
    error,
    fetchTabs,
  ]);
}

export default useProfileTabs;
